import { createBrowserClient } from '@supabase/ssr';

// Debug logging helper
const logDebug = (action, details) => {
    const timestamp = new Date().toISOString();
    console.debug(`[Supabase ${timestamp}] ${action}:`, details);
};

// Performance logging helper
const logTiming = async (operation, fn) => {
    const start = performance.now();
    try {
        const result = await fn();
        const duration = performance.now() - start;
        logDebug(`${operation} completed`, { durationMs: duration.toFixed(2) });
        return result;
    } catch (error) {
        const duration = performance.now() - start;
        logDebug(`${operation} failed`, { durationMs: duration.toFixed(2), error });
        throw error;
    }
};

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Log Supabase configuration for debugging (obscure sensitive data)
// console.log('Supabase Configuration:', {
//     url: supabaseUrl,
//     anonKey: supabaseAnonKey ? 'Key is set (obscured)' : 'Key is not set',
//     urlValid: supabaseUrl?.startsWith('https://'),
//     urlDomain: supabaseUrl?.replace('https://', '').split('.')[0],
//     isProduction: window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1',
//     currentHostname: window.location.hostname
// });

// Test environment variables and client creation
try {
    // console.log('🔧 Creating Supabase client...');
    if (!supabaseUrl) {
        throw new Error('REACT_APP_SUPABASE_URL is not set');
    }
    if (!supabaseAnonKey) {
        throw new Error('REACT_APP_SUPABASE_ANON_KEY is not set');
    }
    // console.log('✅ Environment variables loaded successfully');
} catch (envError) {
    console.error('❌ Environment variable error:', envError.message);
}

// Create Supabase client with error handling
let supabase;
try {
    supabase = createBrowserClient(supabaseUrl, supabaseAnonKey, {
    auth: {
        flowType: 'pkce',
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        session: {
            tokenRefreshThreshold: 120, // Refresh token 120 seconds before expiry
            persistSession: true,
            maxTokenAge: 60 * 30  // 30 minutes
        },
        cookieOptions: {
            name: 'sb-auth',
            path: '/',
            sameSite: 'Lax',  // Changed from 'Strict' to 'Lax' for better compatibility
            secure: true,
            httpOnly: false  // Changed to false since we're setting cookies from JS
        }
    }
});

    // console.log('✅ Supabase client created successfully');
    // console.log('🔗 Supabase URL:', supabase.supabaseUrl);
} catch (clientError) {
    console.error('❌ Failed to create Supabase client:', clientError.message);
    // Create a minimal fallback client for error handling
    const notInitialized = () => Promise.reject(new Error('Supabase client not initialized. Check REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your env.'));
    supabase = {
        auth: {
            getSession: notInitialized,
            onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
            signInWithOAuth: notInitialized,
            signInWithPassword: notInitialized,
            signOut: notInitialized,
            signUp: notInitialized,
            refreshSession: notInitialized,
            exchangeCodeForSession: notInitialized,
            verifyOtp: notInitialized,
            resend: notInitialized,
            resetPasswordForEmail: notInitialized,
            updateUser: notInitialized
        },
        supabaseUrl: supabaseUrl || 'unknown'
    };
}

// Track visibility state (used by checkSession for logging/monitoring)
let wasHidden = false;
let lastActiveTime = Date.now();

// NOTE: Visibility change handling is managed in AuthContext.js to avoid duplicate handlers
// These variables are kept for potential use in checkSession logging

// Add helper functions for session management
export const checkSession = async (forceRefresh = false) => {
    try {
        logDebug('checkSession started', { 
            timestamp: Date.now(),
            forceRefresh,
            wasHidden
        });
        
        const { data, error } = await logTiming('getSession', () => supabase.auth.getSession());
        
        const sessionStatus = data.session ? {
            active: true,
            expiresAt: new Date(data.session.expires_at * 1000).toISOString(),
            timeUntilExpiry: (data.session.expires_at * 1000) - Date.now()
        } : { active: false };
        
        logDebug('Session status', { sessionStatus, error: error?.message });
        
        // Force refresh or refresh if expiring soon
        if (data.session) {
            const tokenExpiryTime = new Date(data.session.expires_at * 1000);
            const timeUntilExpiry = tokenExpiryTime - new Date();
            
            if (forceRefresh || timeUntilExpiry < 120000) { // force or < 2 minutes
                logDebug('Token refresh needed', { 
                    timeUntilExpiry,
                    reason: forceRefresh ? 'forced' : 'expiring'
                });
                const refreshResult = await refreshSession();
                if (refreshResult.error) {
                    logDebug('Refresh failed, redirecting to login', {
                        error: refreshResult.error?.message
                    });
                    // Optionally redirect to login
                    window.location.href = '/login';
                    return { session: null, error: refreshResult.error };
                }
                return refreshResult;
            }
        } else if (wasHidden) {
            // No session after being hidden - likely expired
            logDebug('No session after inactivity, redirecting to login');
            window.location.href = '/login';
            return { session: null, error: new Error('Session expired during inactivity') };
        }
        
        return { session: data.session, error };
    } catch (e) {
        logDebug('Session check error', { error: e?.message, stack: e?.stack });
        // If this was after inactivity, redirect to login
        if (wasHidden) {
            window.location.href = '/login';
        }
        return { session: null, error: e };
    }
};

// Add explicit refresh function
export const refreshSession = async () => {
    try {
        logDebug('refreshSession started', { timestamp: Date.now() });
        const { data, error } = await logTiming('refreshSession', () => supabase.auth.refreshSession());

        if (data?.session) {
            logDebug('Session refreshed', {
                newExpiresAt: new Date(data.session.expires_at * 1000).toISOString(),
                timeUntilExpiry: (data.session.expires_at * 1000) - Date.now()
            });
        } else {
            logDebug('Session refresh failed', { error: error?.message });
        }

        return { data, error };
    } catch (e) {
        logDebug('Session refresh error', { error: e?.message, stack: e?.stack });
        return { data: null, error: e };
    }
};

// Test function to verify Supabase client is working
export const testSupabaseConnection = async () => {
    try {
        logDebug('Testing Supabase connection...');
        // Try a simple authenticated request
        const { data, error } = await supabase.auth.getSession();
        logDebug('Supabase connection test result:', {
            success: !error,
            hasSession: !!data?.session,
            error: error?.message
        });
        return { success: !error, session: data?.session, error };
    } catch (e) {
        logDebug('Supabase connection test failed:', { error: e?.message });
        return { success: false, error: e };
    }
};

export const exchangeCodeForSession = async (code) => {
    try {
        logDebug('Exchanging code for session', { code_present: !!code });
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
            logDebug('Code exchange failed', { error: error.message });
            return { error };
        }
        logDebug('Code exchange successful', { userId: data?.user?.id });
        return { data };
    } catch (e) {
        logDebug('Code exchange error', { error: e.message });
        return { error: e };
    }
};

export default supabase;